// components/common/SearchableDropdown.js
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../icon/Icon';
import './SearchableDropdown.css';

const SearchableDropdown = ({
  value,
  onChange,
  fetchOptions,
  placeholder = "Rechercher...",
  disabled = false,
  required = false,
  idField = "_id",
  displayField = "name",
  subField = null,
  multiple = false,
  maxSelections = null,
  renderOption = null,
  debounceDelay = 300,
  showAddButton = false,
  addButtonIcon = "Add",
  modalComponent: ModalComponent = null,
  modalProps = {},
  onModalClose = null,
  newTab = false,
  newTabUrl = "",
  className = "",
  inputClassName = "",
  icon = "Search",
  label = null,
  helperText = null,
  error = null,
  success = null,
  variant = "default",
  size = "md",
  pill = false,
  shadow = true,
  clearable = true,
  searchOnFocus = false,
  highlightMatches = true,
  loadingMessage = "Recherche en cours...",
  noResultsMessage = "Aucun résultat trouvé",
  emptyMessage = "Commencez à taper pour rechercher",
  ...rest
}) => {
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const searchRef = useRef(null);
  const dropdownRef = useRef(null);       // the input/wrapper element
  const portalRef = useRef(null);         // the portaled dropdown list
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  const getDisplayValue = useCallback((item) => {
    if (!item) return '';
    let display = '';
    if (displayField.includes('.')) {
      const fields = displayField.split('.');
      let value = item;
      for (const field of fields) {
        value = value?.[field];
        if (!value) break;
      }
      display = value || '';
    } else {
      display = item[displayField] || '';
    }
    return display.toString();
  }, [displayField]);

  useEffect(() => {
    if (value) {
      if (multiple && Array.isArray(value)) {
        setSelectedItems(value);
      } else if (!multiple && value && typeof value === 'object') {
        setSelectedItems([value]);
        setSearchTerm(getDisplayValue(value));
      }
    } else {
      setSelectedItems([]);
      if (!multiple) setSearchTerm('');
    }
  }, [value, multiple, idField, getDisplayValue]);

  const extractDataFromResponse = useCallback((result) => {
    if (!result) return [];
    if (Array.isArray(result)) return result;
    if (result.data && Array.isArray(result.data)) return result.data;
    if (result.data?.devis && Array.isArray(result.data.devis)) return result.data.devis;
    if (result.success && result.data && Array.isArray(result.data)) return result.data;
    if (result.clients && Array.isArray(result.clients)) return result.clients;
    if (result.devis && Array.isArray(result.devis)) return result.devis;
    if (result.data && typeof result.data === 'object') {
      const firstArrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
      if (firstArrayKey) return result.data[firstArrayKey];
    }
    return [];
  }, []);

  const handleSelect = useCallback((option) => {
    if (multiple) {
      if (maxSelections && selectedItems.length >= maxSelections) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000);
        return;
      }
      const isAlreadySelected = selectedItems.some((item) => item[idField] === option[idField]);
      if (!isAlreadySelected) {
        const newSelectedItems = [...selectedItems, option];
        setSelectedItems(newSelectedItems);
        onChange(newSelectedItems);
        setSearchTerm('');
      }
    } else {
      setSelectedItems([option]);
      onChange(option);
      setShowDropdown(false);
      setIsFocused(false);
      setSearchTerm(getDisplayValue(option));
    }
    setActiveIndex(-1);
  }, [multiple, maxSelections, selectedItems, idField, onChange, getDisplayValue]);

  const loadOptions = useCallback(async (search = '') => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      if (typeof fetchOptions !== 'function') {
        setOptions([]);
        return;
      }
      const result = await fetchOptions(search, signal);
      setOptions(extractDataFromResponse(result));
      setActiveIndex(-1);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching options:', error);
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchOptions, extractDataFromResponse]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (showDropdown && searchTerm !== null && searchTerm !== undefined) {
      debounceRef.current = setTimeout(() => loadOptions(searchTerm), debounceDelay);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, showDropdown, debounceDelay, loadOptions]);

  useEffect(() => {
    if (showDropdown && searchOnFocus && options.length === 0 && !loading) {
      loadOptions(searchTerm || '');
    }
  }, [showDropdown, searchTerm, options.length, loadOptions, loading, searchOnFocus]);

  // Click outside — must check BOTH the anchor and the portaled dropdown,
  // since the dropdown no longer lives inside dropdownRef's DOM subtree.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedAnchor = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedPortal = portalRef.current && portalRef.current.contains(event.target);
      if (!clickedAnchor && !clickedPortal) {
        setShowDropdown(false);
        setIsFocused(false);
        if (!multiple && !selectedItems.length) setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [multiple, selectedItems]);

  // Position the portaled dropdown against the anchor, and keep it in sync
  // with scroll/resize anywhere in the page (not just the immediate parent).
  const updatePosition = useCallback(() => {
    if (!dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    setDropdownRect({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    });
  }, []);

  useLayoutEffect(() => {
    if (!showDropdown) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, true); // capture: catches scroll on any ancestor
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown, updatePosition]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        setShowDropdown(false);
        setIsFocused(false);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && options[activeIndex]) {
          handleSelect(options[activeIndex]);
        } else if (searchTerm && options.length > 0 && !selectedItems.length) {
          handleSelect(options[0]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (showDropdown && options.length > 0) {
          setActiveIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (showDropdown && options.length > 0) {
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
        }
        break;
      case 'Tab':
        if (showDropdown) setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  const handleAddClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (newTab && newTabUrl) {
      window.open(newTabUrl, '_blank');
    } else if (ModalComponent && !newTab) {
      setShowModal(true);
      setModalKey((prev) => prev + 1);
    }
    setShowDropdown(false);
  };

  const handleCloseModal = (newItem = null) => {
    setShowModal(false);
    if (newItem) {
      handleSelect(newItem);
      loadOptions(searchTerm);
    }
    if (modalProps.onClose && typeof modalProps.onClose === 'function') modalProps.onClose(newItem);
    if (newItem && modalProps.onSuccess && typeof modalProps.onSuccess === 'function') modalProps.onSuccess(newItem);
    if (onModalClose) onModalClose(newItem);
  };

  const handleRemove = useCallback((itemToRemove) => {
    const newSelectedItems = selectedItems.filter((item) => item[idField] !== itemToRemove[idField]);
    setSelectedItems(newSelectedItems);
    onChange(multiple ? newSelectedItems : null);
  }, [selectedItems, idField, multiple, onChange]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    if (!showDropdown) setShowDropdown(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setShowDropdown(true);
    if (options.length === 0 && !loading) loadOptions(searchTerm);
  };

  const handleBlur = () => {
    setTimeout(() => setIsFocused(false), 200);
  };

  const getSubDisplayValue = useCallback((item) => {
    if (!item || !subField) return '';
    let subValue = '';
    if (subField.includes('.')) {
      const subFields = subField.split('.');
      let subVal = item;
      for (const field of subFields) {
        subVal = subVal?.[field];
        if (!subVal) break;
      }
      subValue = subVal || '';
    } else {
      subValue = item[subField] || '';
    }
    return subValue.toString();
  }, [subField]);

  const highlightText = (text, search) => {
    if (!text || !search || !highlightMatches) return text;
    const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="sd-mark">{part}</mark>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  useEffect(() => {
    if (!multiple && selectedItems[0] && searchRef.current) {
      const displayValue = getDisplayValue(selectedItems[0]);
      if (searchRef.current.value !== displayValue) {
        searchRef.current.value = displayValue;
      }
    }
  }, [selectedItems, multiple, getDisplayValue]);

  const getInputDisplayValue = useMemo(() => {
    if (!multiple && selectedItems[0]) return getDisplayValue(selectedItems[0]);
    return searchTerm;
  }, [multiple, selectedItems, searchTerm, getDisplayValue]);

  const clearSelection = () => {
    setSelectedItems([]);
    onChange(multiple ? [] : null);
    setSearchTerm('');
    setShowDropdown(true);
    setIsFocused(true);
    if (searchRef.current) searchRef.current.focus();
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'actif':
      case 'active':
      case 'validé':
      case 'completed':
        return 'success';
      case 'inactif':
      case 'inactive':
      case 'annulé':
      case 'cancelled':
        return 'danger';
      case 'en attente':
      case 'pending':
      case 'en cours':
        return 'warning';
      case 'brouillon':
      case 'draft':
        return 'secondary';
      default:
        return 'light';
    }
  };

  const defaultRenderOption = useCallback((option, index) => {
    const mainDisplay = getDisplayValue(option);
    const subDisplay = getSubDisplayValue(option);
    const isSelected = selectedItems.some((item) => item[idField] === option[idField]);
    const isActive = index === activeIndex;

    return (
      <div className={`sd-option${isActive ? ' sd-option-active' : ''}${isSelected ? ' sd-option-selected' : ''}`}>
        <div className="sd-option-indicator">
          {isSelected ? (
            <Icon icon="CheckCircle" className="sd-option-check" />
          ) : (
            <span className="sd-option-circle" />
          )}
        </div>
        <div className="sd-option-content">
          <div className="sd-option-main">
            {highlightMatches && searchTerm ? highlightText(mainDisplay, searchTerm) : mainDisplay}
          </div>
          {subDisplay && <div className="sd-option-sub">{subDisplay}</div>}
          <div className="sd-option-meta">
            {option.status && (
              <span className={`sd-badge sd-badge-${getStatusVariant(option.status)}`}>{option.status}</span>
            )}
            {option.reference && <span className="sd-badge sd-badge-outline">Réf: {option.reference}</span>}
            {option.totalTTC !== undefined && <span className="sd-option-price">{option.totalTTC}€</span>}
          </div>
        </div>
      </div>
    );
  }, [getDisplayValue, getSubDisplayValue, selectedItems, idField, searchTerm, activeIndex, highlightMatches]);

  const getAddButtonTooltip = () => {
    if (newTab) return `Ouvrir ${newTabUrl} dans un nouvel onglet`;
    return modalProps.title || "Ajouter un nouvel élément";
  };

  const atMaxSelections = maxSelections && selectedItems.length >= maxSelections;

  const dropdownMenu = showDropdown && dropdownRect ? (
    <div
      ref={portalRef}
      className={`sd-dropdown ${shadow ? 'sd-dropdown-shadow-lg' : 'sd-dropdown-shadow-sm'}`}
      style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
    >
      <div className="sd-dropdown-header">
        <span className="sd-dropdown-count-text">
          {loading ? (
            <span className="sd-dropdown-loading">
              <span className="sd-spinner sd-spinner-sm" aria-hidden="true" />
              {loadingMessage}
            </span>
          ) : (
            <>
              <span className="sd-dropdown-count">{options.length}</span> résultat(s)
              {searchTerm && <span className="sd-dropdown-term"> pour "{searchTerm}"</span>}
            </>
          )}
        </span>

        {showAddButton && (ModalComponent || newTab) && !loading && (
          <button type="button" className="sd-dropdown-add" onClick={handleAddClick} title={getAddButtonTooltip()}>
            <Icon icon={addButtonIcon} size="sm" />
          </button>
        )}
      </div>

      <div className="sd-dropdown-list">
        {!loading && options.length === 0 ? (
          <div className="sd-empty-state">
            <div className="sd-empty-icon">
              <Icon icon="SearchOff" />
            </div>
            <div className="sd-empty-text">{searchTerm ? noResultsMessage : emptyMessage}</div>
            {showAddButton && (ModalComponent || newTab) && (
              <button type="button" className="sd-empty-add-btn" onClick={handleAddClick}>
                <Icon icon={addButtonIcon} size="sm" />
                {newTab ? "Créer dans un nouvel onglet" : "Créer"}
              </button>
            )}
          </div>
        ) : (
          options.map((option, index) => (
            <div
              key={option[idField] || index}
              className="sd-option-row"
              onClick={() => handleSelect(option)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {renderOption ? renderOption(option, index) : defaultRenderOption(option, index)}
            </div>
          ))
        )}
      </div>

      {options.length > 5 && (
        <div className="sd-dropdown-footer">Utilisez ↑↓ pour naviguer, Entrée pour sélectionner</div>
      )}
    </div>
  ) : null;

  return (
    <div
      className={`sd-wrapper sd-size-${size} sd-variant-${variant} ${pill ? 'sd-pill' : ''} ${disabled ? 'sd-disabled' : ''} ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="sd-label">
          {label}
          {required && <span className="sd-required">*</span>}
        </label>
      )}

      <div className="sd-input-wrap">
        <div
          className={`sd-input-group ${isFocused ? 'sd-focused' : ''} ${error ? 'sd-error-state' : success ? 'sd-success-state' : ''} ${shadow && isFocused ? 'sd-shadow' : ''}`}
        >
          <span className="sd-input-icon">
            <Icon icon={icon || "Search"} className={loading ? 'sd-icon-loading' : 'sd-icon-muted'} />
          </span>

          <input
            ref={searchRef}
            type="text"
            value={getInputDisplayValue}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            onClick={() => setShowDropdown(true)}
            autoComplete="off"
            className={`sd-input ${inputClassName}`}
            {...rest}
          />

          <div className="sd-actions">
            {loading && <span className="sd-spinner" aria-hidden="true" />}

            {!loading && clearable && !multiple && selectedItems.length > 0 && (
              <button
                type="button"
                className="sd-clear-btn"
                onClick={clearSelection}
                disabled={disabled}
                aria-label="Effacer la sélection"
              >
                <Icon icon="Close" size="18" />
              </button>
            )}

            {showAddButton && (ModalComponent || newTab) && (
              <div className="sd-add-btn-wrap">
                <button
                  type="button"
                  className={`sd-add-btn ${newTab ? 'sd-add-btn-outline' : 'sd-add-btn-solid'}`}
                  onClick={handleAddClick}
                  disabled={disabled}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  title={getAddButtonTooltip()}
                >
                  <Icon icon={addButtonIcon} size="lg" />
                </button>
                {showTooltip && (
                  <div className="sd-tooltip">
                    {atMaxSelections ? `Maximum ${maxSelections} sélections autorisées` : getAddButtonTooltip()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {(helperText || error || success) && (
          <div className="sd-messages">
            {helperText && <div className="sd-helper-text">{helperText}</div>}
            {error && (
              <div className="sd-error-text">
                <Icon icon="Error" size="sm" /> {error}
              </div>
            )}
            {success && (
              <div className="sd-success-text">
                <Icon icon="CheckCircle" size="sm" /> {success}
              </div>
            )}
          </div>
        )}
      </div>

      {multiple && selectedItems.length > 0 && (
        <div className="sd-chips-wrap">
          <div className="sd-chips">
            {selectedItems.map((item, index) => (
              <span key={`${item[idField]}-${index}`} className={`sd-chip ${pill ? 'sd-chip-pill' : ''}`}>
                <span className="sd-chip-main">{getDisplayValue(item)}</span>
                {getSubDisplayValue(item) && <span className="sd-chip-sub">· {getSubDisplayValue(item)}</span>}
                <button
                  type="button"
                  className="sd-chip-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(item);
                  }}
                  aria-label="Retirer"
                >
                  <Icon icon="Close" size="12" />
                </button>
              </span>
            ))}
          </div>
          {maxSelections && (
            <div className="sd-chips-count">
              {selectedItems.length} / {maxSelections} sélectionné(s)
            </div>
          )}
        </div>
      )}

      {typeof document !== 'undefined' && dropdownMenu && createPortal(dropdownMenu, document.body)}

      {ModalComponent && !newTab && showModal && (
        <ModalComponent key={modalKey} show={showModal} onHide={() => handleCloseModal(null)} onClose={handleCloseModal} {...modalProps} />
      )}
    </div>
  );
};

export default SearchableDropdown;